import { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { handleFetchRequestsByUnit } from "../../../api/autorequests";
import { useQuery } from "@tanstack/react-query";
import AllAutoRequestsDrawer from "./AllRequestsDrawer";
import UnitWidget from "./UnitWidget";

// Component definition
export default function UnitRequests({ dateRange }: any) {
  const scrollRef = useRef(null);

  const [isVisible, setIsVisible] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const { data: unitRequests } = useQuery({
    queryKey: ["unitRequests", dateRange?.startDate, dateRange?.endDate],
    queryFn: () =>
      handleFetchRequestsByUnit({
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      }),
  });

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollability = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      const { scrollWidth, clientWidth, scrollLeft } = el;

      const isOverflowing = scrollWidth > clientWidth;

      setCanScrollLeft(isOverflowing && scrollLeft > 5);

      setCanScrollRight(
        isOverflowing && Math.ceil(scrollLeft) + clientWidth < scrollWidth - 5
      );
    }
  }, []);

  const scroll = (direction: any) => {
    const el: any = scrollRef.current;
    if (el) {
      const scrollAmount = el.clientWidth * 0.8;
      const newScrollLeft =
        el.scrollLeft + (direction === "left" ? -scrollAmount : scrollAmount);

      el.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
      setTimeout(checkScrollability, 50);
    }
  };

  const handleSelect = (item: any) => {
    setIsVisible(true);
    setSelectedUnit(item);
  };

  useEffect(() => {
    const el: any = scrollRef.current;

    checkScrollability();

    el?.addEventListener("scroll", checkScrollability);
    window.addEventListener("resize", checkScrollability);

    return () => {
      el?.removeEventListener("scroll", checkScrollability);
      window.removeEventListener("resize", checkScrollability);
    };
  }, [checkScrollability]);

  return (
    <div className=" bg-gray-100  flex flex-col items-center">
      <div className="w-full">
        <div className="relative w-full overflow-hidden py-2 bg-white  border-gray-200">
          <button
            onClick={() => scroll("left")}
            className={`absolute top-1/2 -translate-y-1/2 left-0 z-10 p-3 ml-2 bg-blue-500 text-white rounded-full shadow-xl hover:bg-blue-700 transition duration-300 ease-in-out
          ${
            canScrollLeft
              ? "opacity-100 pointer-events-auto scale-100"
              : "opacity-0 pointer-events-none scale-90"
          }`}
            aria-label="Scroll Left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div
            ref={scrollRef}
            className="flex space-x-4  px-1 overflow-x-scroll scrollbar-hide"
          >
            {unitRequests?.data?.map((item: any) => (
              <UnitWidget
                key={item._id}
                item={item}
                handleSelect={handleSelect}
              />
            ))}
            <div className="">
              {!unitRequests?.data ||
                (unitRequests?.data?.length === 0 && (
                  <div className="flex space-x-6  px-1 overflow-x-scroll scrollbar-hide">
                    {Array.from({ length: 10 }).map((_, index) => (
                      <UnitWidget
                        key={index}
                        item={{
                          _id: index,
                          businessUnit: "Business Unit",
                          count: 0,
                        }}
                        handleSelect={handleSelect}
                      />
                    ))}
                  </div>
                ))}
            </div>
          </div>

          <button
            onClick={() => scroll("right")}
            className={`absolute top-1/2 -translate-y-1/2 right-0 z-10 p-3 mr-2 bg-blue-500 text-white rounded-full shadow-xl hover:bg-blue-700 transition duration-300 ease-in-out
          ${
            canScrollRight
              ? "opacity-100 pointer-events-auto scale-100"
              : "opacity-0 pointer-events-none scale-90"
          }`}
            aria-label="Scroll Right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        }
      `}</style>
        </div>
      </div>
      {isVisible && (
        <AllAutoRequestsDrawer
          isVisible={isVisible}
          setVisible={setIsVisible}
          businessUnitId={
            selectedUnit?.businessUnitId === null
              ? ""
              : selectedUnit?.businessUnitId
          }
        />
      )}
    </div>
  );
}
